import React, { useEffect, useState } from "react";
import { RefreshCw, PlayCircle } from "lucide-react";
import { SCSection, fmtDate } from "./Common";
import { scAdminOverview, scAdminLatestBatches, scAdminRunJob } from "../../../lib/sifcontrolApi";
import { toast } from "sonner";

const Tile = ({ label, value, sub, testId }) => (
    <div className="rounded-2xl border border-oth-line/70 bg-white p-5" data-testid={testId}>
        <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">{label}</div>
        <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-oth-ink mt-1.5">{value}</div>
        {sub && <div className="mt-1 text-[11px] font-body text-oth-ink3">{sub}</div>}
    </div>
);

const StatusPill = ({ status }) => {
    const map = {
        completed: "bg-green-100 text-green-800",
        completed_with_errors: "bg-yellow-100 text-yellow-800",
        failed: "bg-red-100 text-red-800",
        running: "bg-blue-100 text-blue-800",
    };
    const cls = map[status] || "bg-oth-bg-2 text-oth-ink3";
    return <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-body uppercase tracking-wider ${cls}`}>{status || "never run"}</span>;
};

const JobCard = ({ label, dataType, batch, onRun, running }) => (
    <div className="rounded-2xl border border-oth-line/70 bg-white p-5" data-testid={`sc-job-card-${dataType}`}>
        <div className="flex items-start justify-between gap-3">
            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">{label}</div>
                <div className="font-display text-lg font-semibold tracking-tight text-oth-ink mt-1">{fmtDate(batch?.started_at) || "Never run"}</div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <StatusPill status={batch?.status} />
                    {batch?.records_inserted !== undefined && (
                        <span className="text-[11px] font-body text-oth-ink3">
                            +{batch.records_inserted} / ~{batch.records_updated} / skip {batch.records_skipped}
                        </span>
                    )}
                </div>
            </div>
            <button
                onClick={() => onRun(dataType)}
                disabled={running === dataType}
                className="btn-pill btn-pill-dark !py-1.5 !px-3 !text-xs shrink-0"
                data-testid={`sc-job-run-${dataType}`}
            >
                {running === dataType ? <RefreshCw size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                Run now
            </button>
        </div>
        {batch?.error_message && (
            <div className="mt-3 text-[11px] font-body text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid={`sc-job-error-${dataType}`}>
                {batch.error_message}
            </div>
        )}
    </div>
);

export default function OverviewTab() {
    const [overview, setOverview] = useState(null);
    const [latest, setLatest] = useState({});
    const [running, setRunning] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [o, l] = await Promise.all([scAdminOverview(), scAdminLatestBatches()]);
            setOverview(o);
            setLatest(l);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const run = async (dataType) => {
        setRunning(dataType);
        try {
            const batch = await scAdminRunJob(dataType);
            setLatest((p) => ({ ...p, [dataType]: batch }));
            if (batch.status === "failed") toast.error(`${dataType.toUpperCase()} job failed: ${batch.error_message || "see details"}`);
            else if (batch.status === "completed_with_errors") toast.warning(`${dataType.toUpperCase()} completed with errors.`);
            else toast.success(`${dataType.toUpperCase()} ingestion completed.`);
            const o = await scAdminOverview();
            setOverview(o);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Job failed");
        } finally {
            setRunning(null);
        }
    };

    return (
        <div data-testid="sc-admin-overview">
            <SCSection
                title="SIF Control · Overview"
                subtitle="Master database counts and latest ingestion status."
                right={
                    <button onClick={load} className="btn-pill !text-xs !py-1.5 !px-3" data-testid="sc-overview-refresh">
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                }
                testId="sc-overview-header"
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <Tile label="AMCs" value={overview?.total_amcs ?? "—"} testId="sc-tile-amcs" />
                <Tile label="Active SIFs" value={overview?.active_sifs ?? "—"} sub={overview ? `of ${overview.total_sifs} total` : ""} testId="sc-tile-sifs" />
                <Tile label="AMC users" value={overview?.total_amc_users ?? "—"} testId="sc-tile-amc-users" />
                <Tile label="NAV observations" value={overview?.nav_observations ?? "—"} sub={overview ? `AUM: ${overview.aum_observations} · TER: ${overview.ter_observations}` : ""} testId="sc-tile-obs" />
            </div>

            <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-3">Data jobs</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <JobCard label="NAV — latest" dataType="nav" batch={latest.nav} onRun={run} running={running} />
                <JobCard label="AUM — average" dataType="aum" batch={latest.aum} onRun={run} running={running} />
                <JobCard label="TER — expense ratio" dataType="ter" batch={latest.ter} onRun={run} running={running} />
            </div>

            <div className="mt-6 rounded-2xl border border-dashed border-oth-line/70 bg-oth-bg-2/40 p-4 text-[12px] font-body text-oth-ink2" data-testid="sc-overview-help">
                Ingestion uses Browserless (headless Chrome) because AMFI's SIF pages are JavaScript-rendered. If a job reports "No table rows parsed" or a
                fetch failure, check the Browserless token in <b>Settings</b>. Runs are idempotent — repeat safely.
            </div>
        </div>
    );
}
